import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Page, Input, Tabs } from 'equasion-lib';
import ActionTopBar from '@/lib/shared-coms/ActionTopBar';
import { FiSearch } from 'react-icons/fi';
import {
  pickVerifyListTab,
  pickVerifyListTabFromParam,
  pickVerifyPickerName
} from '@/lib/pickVerifyShip';
import ShippingLaneSelect from '@/lib/shared-coms/ShippingLaneSelect';
import SalesOrderList from './com/SalesOrderList';
import './SalesOrders.scss';

// Pick → Verify → Ship (Option 2) companies get a Verify tab between Pick and Ship. `as any` where
// it is passed: equasion's TabsDataOptions wants a `content` that a menu-only tab bar never renders
// (the same cast admin and manager use).
const PICK_VERIFY_TABS = [
  {
    label: 'Pick',
    key: 'picking'
  },
  {
    label: 'Verify',
    key: 'verify'
  },
  {
    label: 'Ship',
    key: 'shipping'
  }
];

/**
 * The SalesOrderList props for each of an Option-2 company's tabs; Ship needs none (the list's
 * default `vendorStatus === 'shipping'` filter is already right). An Option-1 company never reaches
 * this — its list is built from exactly today's tabs, parse and props.
 */
const pickVerifyListProps = (tab) => {
  if (tab === 'verify') {
    return {
      filter: (order) => pickVerifyListTab(order) === 'verify',
      partialTitle: 'partially verified',
      readyTitle: 'ready to verify'
    };
  }
  if (tab === 'picking') {
    return {
      filter: (order) => pickVerifyListTab(order) === 'picking',
      // An Option-2 Pick records no inventory until Verify, so "partially picked" can't come from
      // PICKED units alone: an order someone has claimed is in progress too.
      isInProgress: (order) => !!order.partiallyPicked || !!pickVerifyPickerName(order)
    };
  }
  return {};
};

const SalesOrders = ({
  salesOrders,
  usesTotes,
  usesPickVerifyShip = false,
  shippingLanes = [],
  myShippingLane = null,
  savingShippingLane = false,
  onShippingLaneChange = (_lane) => {}
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = usesPickVerifyShip
    ? pickVerifyListTabFromParam(searchParams.get('tab'))
    : searchParams.get('tab') === 'shipping'
      ? 'shipping'
      : 'picking';
  const [search, setSearch] = useState('');
  const filteredSalesOrders = salesOrders.filter(({ vendorSalesOrderNumber, tote }) => {
    if (vendorSalesOrderNumber.includes(search)) return true;
    // With totes off the row shows no tote sub-label, so matching on a tote name would surface rows
    // with no visible reason for being there. Evaluated per render from the live flag — the
    // SO-number clause above is unchanged either way, so this is a strict subset of today's filter.
    if (!usesTotes) return false;
    // `tote.number` does not exist on `type Tote` (id / name / createdAt / orderId / companyId) —
    // the old `tote?.name || tote?.number` fallback was always dead.
    return (tote?.name || '').toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Page className="sales-orders action-page">
      <ActionTopBar action="Sales Orders" backPath="/">
        {shippingLanes.length > 0 && (
          <ShippingLaneSelect
            lanes={shippingLanes}
            myLane={myShippingLane}
            saving={savingShippingLane}
            onChange={onShippingLaneChange}
          />
        )}
      </ActionTopBar>

      <div className="action-page-body">
        <div className="top-filters">
          {usesPickVerifyShip ? (
            <Tabs
              menuOnly
              selectedKey={selectedTab}
              onTabChange={(value) =>
                setSearchParams(value === 'picking' ? {} : { tab: String(value) })
              }
              data={PICK_VERIFY_TABS as any}
            />
          ) : (
            <Tabs
              menuOnly
              selectedKey={selectedTab}
              onTabChange={(value) =>
                setSearchParams(value === 'shipping' ? { tab: 'shipping' } : {})
              }
              data={[
                {
                  label: 'Pick',
                  key: 'picking'
                },
                {
                  label: 'Ship',
                  key: 'shipping'
                }
              ]}
            />
          )}
          <Input
            form={false}
            label="Search"
            iconAfter={<FiSearch />}
            value={search}
            onChange={({ value }) => setSearch(value)}
          />
        </div>
        <SalesOrderList
          salesOrders={filteredSalesOrders}
          selectedStatus={selectedTab}
          usesTotes={usesTotes}
          {...(usesPickVerifyShip ? pickVerifyListProps(selectedTab) : {})}
        />
      </div>
    </Page>
  );
};

export default SalesOrders;
